import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

export type TabDefinition = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** data-tour attribute value */
  tourId?: string;
  /** If true, show unread badge on this tab */
  showUnreadBadge?: boolean;
};

type Props = {
  tabs: readonly TabDefinition[];
  unreadCount?: number;
};

export function BottomTabBar({ tabs, unreadCount = 0 }: Props) {
  return (
    <nav
      data-tour="tab-bar"
      className="fixed left-0 right-0 z-50 mx-auto w-[90%] max-w-sm flex items-center justify-around rounded-[24px] border border-border/50 bg-surface/95 px-2 py-2 shadow-xl backdrop-blur-xl"
      style={{ bottom: 'calc(1rem + max(34px, env(safe-area-inset-bottom, 0px)))' }}
    >
      {tabs.map(({ to, label, icon: Icon, tourId, showUnreadBadge }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          {...(tourId ? { "data-tour": tourId } : {})}
          className={({ isActive }) =>
            `relative flex flex-1 flex-col items-center gap-1 rounded-xl py-1.5 transition-all ${isActive ? "text-accent bg-accent/10" : "text-muted hover:bg-surface2"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                {showUnreadBadge && unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-danger border border-surface shadow-sm" aria-hidden="true" />
                )}
              </div>
              <span className="text-[10px] font-semibold tracking-wide">{label}</span>
              {showUnreadBadge && unreadCount > 0 && (
                <span className="sr-only">{unreadCount} unread message{unreadCount !== 1 ? "s" : ""}</span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
